import React, { useRef, useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";

// Componentes de preguntas
import TextQuestion from "../../components/questions/textQuestion/textQuestion";
import DropdownQuestion from "../../components/questions/dropdownQuestion/dropdownQuestion";
import DropdownWithOther from "../../components/questions/dropdownWithOther/dropdownWithOther";
import ABQuestion from "../../components/questions/abQuestion/abQuestion";
import MultipleChoiceQuestion from "../../components/questions/multipleChoiceQuestion/multipleChoiceQuestion";
import SingleSelectWithFollowUp from "../../components/questions/singleSelectWithFollowUp/singleSelectWithFollowUp";
import RatingQuestion from "../../components/questions/ratingQuestion/ratingQuestion";

// Datos de opciones
import mexicoStates from "../../data/mexicoStates";
import ageRanges from "../../data/ageRanges";
import maritalStatus from "../../data/maritalStatus";
import commuteTime from "../../data/commuteTime";
import workScheme from "../../data/workScheme";
import workTimeType from "../../data/workTimeType";
import workHours from "../../data/workHours";
import diseases from "../../data/diseases";
import familyDiseases from "../../data/familyDiseases";
import waterIntake from "../../data/waterIntake";
import sleepHours from "../../data/sleepHours";
import exerciseLocation from "../../data/exerciseLocation";
import exerciseHours from "../../data/exerciseHours";
import eatingOut from "../../data/eatingOut";
import junkFood from "../../data/junkFood";
import fruitsVegetables from "../../data/fruitsVegetables";
import cigarettesPerWeek from "../../data/cigarettesPerWeek";
import alcoholFrequency from "../../data/alcoholFrequency";
import addictions from "../../data/addictions";
import emotionalWellbeingHours from "../../data/emotionalWellbeingHours";
import frequencyOptions from "../../data/frequencyOptions";
import frequencyFinancialStress from "../../data/frequencyFinancialStress";
import frequencyFriends from "../../data/frequencyFriends";
import frequencySocial from "../../data/frequencySocial";
import healthAutoEvaluation from "../../data/healthAutoEvaluation";
import healthChangesInteres from "../../data/healthChangesInteres";
import healtCheck from "../../data/healtCheck";

// APIs
import usersApi from "../../api/usersApi";
import responsesApi from "../../api/responsesApi";
import questionnaireScopeApi from "../../api/questionnaireScopeApi";

import "../../styles/hra.css";
const PAGE_SIZE = 5;

const HRA = ({ updateProgress }) => {
  const { questionnaire, idEmpresa, applicationId } = useParams();
  const navigate = useNavigate();

  const questionsRef = useRef([]);
  const [, setAnsweredState] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentPage, setCurrentPage] = useState(0);

  const [companyJobAreas, setCompanyJobAreas] = useState([]);
  const [companyName, setCompany] = useState({ name: "" });

  const isQuestionAnswered = (question, value) => {
    if (value === undefined || value === null) return false;
    if (Array.isArray(value)) {
      if (!value[0]) return false;
      if (question.followUp && value[0] === question.followUp.trigger) {
        return Boolean(value[1]);
      }
      return true;
    }
    return value !== "";
  };

  // Fetch scope jobAreas y nombre (branch ObjectId o company UUID)
  useEffect(() => {
    const fetch = async () => {
      try {
        const scope = await questionnaireScopeApi.getById(idEmpresa);
        setCompanyJobAreas(scope.jobAreas || []);
        setCompany(scope);
      } catch (e) {
        console.error("Error cargando empresa/sucursal:", e);
      }
    };
    fetch();
  }, [idEmpresa]);

  // Guardar respuesta y marcar como respondida
  const markQuestionAsAnswered = useCallback(
    (index, field, value) => {
      setAnswers(prev => ({ ...prev, [field]: value ?? "No respondida" }));
      setAnsweredState(prev => {
        const next = [...prev];
        next[index] = true;
        return next;
      });
      updateProgress();
    },
    [updateProgress]
  );

  // Handlers de paginación
  const handlePrev = () => {
    setCurrentPage(p => Math.max(p - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNext = () => {
    setCurrentPage(p => Math.min(p + 1, totalPages - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Envío final
  const handleSend = async () => {
    const firstUnanswered = filteredQuestions.find(
      (q) => !isQuestionAnswered(q, answers[q.field])
    );
    if (firstUnanswered) {
      const idx = questions.findIndex((q) => q.field === firstUnanswered.field);
      questionsRef.current[idx]?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    try {
      const email = answers.email;
      const userResp = await usersApi.createFreeUser({
        username: email,
        password: "",
        role: "user",
        company: idEmpresa
      });
      const payload = {
        userId: userResp.user._id,
        companyId: idEmpresa,
        questionnaireName: questionnaire,
        answers,
        timestamp: new Date().toISOString(),
        applicationId: applicationId || null,
      };
      const res = await responsesApi.createHRAResponse(payload);
      navigate('/hra/results', { state: { response: res.response } });
    } catch (e) {
      const serverMsg = e.response?.data?.message;
      alert(serverMsg || "Error enviando los datos. Intenta de nuevo más tarde.");
      console.error("Error enviando los datos:", e);
    }
  };


  // Lista de preguntas
  const questions = [
    { Component: DropdownQuestion, question: "¿En qué estado de la República vives?", options: mexicoStates, field: "residenceState" },
    { Component: TextQuestion, question: "¿Cuál es tu correo electrónico?", field: "email", type: "email" },
    { Component: DropdownQuestion, question: "¿Cuál es tu área de trabajo?", options: companyJobAreas, field: "jobArea" },
    { Component: ABQuestion, question: "¿Cuál es tu sexo biológico?", optionA: "Masculino", optionB: "Femenino", field: "biologicalSex" },
    { Component: DropdownQuestion, question: "¿Cuál es tu rango de edad?", options: ageRanges, field: "ageRange" },
    { Component: TextQuestion, question: "¿Cuál es tu altura en centímetros? (Ejemplo: 160)", field: "height", type: "height" },
    { Component: TextQuestion, question: "¿Cuál es tu peso actual en kilogramos? (Ejemplo: 50)", field: "weight", type: "weight" },
    { Component: ABQuestion, question: "¿Has mantenido un peso similar en los últimos 6 meses? (+/- 2 kilos)?", optionA: "Sí", optionB: "No", field: "keepWeight" },
    { Component: DropdownQuestion, question: "¿Cuál es tu estado civil?", options: maritalStatus, field: "maritalStatus" },
    { Component: ABQuestion, question: "¿Tienes hijos?", optionA: "Sí", optionB: "No", field: "hasChildren" },
    { Component: DropdownQuestion, question: "¿Cuánto tiempo haces de trayecto al trabajo?", options: commuteTime, field: "commuteTime" },
    { Component: DropdownQuestion, question: "¿Cuál es tu esquema de trabajo?", options: workScheme, field: "workScheme" },
    { Component: DropdownQuestion, question: "¿Cuál es tu tipo de jornada laboral?", options: workTimeType, field: "workType" },
    { Component: DropdownQuestion, question: "¿Cuántas horas trabajas a la semana?", options: workHours, field: "workHours" },
    { Component: ABQuestion, question: "¿Trabajas los fines de semana?", optionA: "Sí", optionB: "No", field: "worksWeekend" },
    { Component: MultipleChoiceQuestion, question: "¿Has padecido alguna de las siguientes enfermedades?", options: diseases, field: "diseases" },
    { Component: MultipleChoiceQuestion, question: "¿Tienes algún familiar cercano con alguno de los siguientes padecimientos?", options: familyDiseases, field: "familyDiseases" },
    { Component: DropdownQuestion, question: "¿Cuántos vasos de agua tomas al día?", options: waterIntake, field: "waterIntake" },
    { Component: DropdownQuestion, question: "¿Cuántas horas duermes al día?", options: sleepHours, field: "sleepHours" },
    { Component: ABQuestion, question: "¿Haces algún deporte o actividad física?", optionA: "Sí", optionB: "No", field: "physicalActivity" },
    { Component: DropdownWithOther, question: "¿En dónde haces ejercicio?", options: exerciseLocation, field: "exerciseLocation" },
    { Component: DropdownQuestion, question: "¿Cuántas horas a la semana haces ejercicio?", options: exerciseHours, field: "exerciseHours" },
    { Component: TextQuestion, question: "¿Cuántas horas CONTINUAS pasas sentado SIN LEVANTARTE en el trabajo?", field: "sitOnWork", type: "numeric" },
    { Component: TextQuestion, question: "¿Cuántas horas permaneces sentado de forma CONTINUA mientras comes o estás en el comedor?", field: "sitOnEat", type: "numeric" },
    { Component: TextQuestion, question: "¿Cuántas horas CONTINUAS pasas sentado SIN LEVANTARTE en el traslado?", field: "sitOnJourney", type: "numeric" },
    { Component: TextQuestion, question: "¿Cuántas horas CONTINUAS pasas sentado SIN LEVANTARTE en casa?", field: "sitOnHome", type: "numeric" },
    { Component: DropdownQuestion, question: "¿Cuántas frutas o verduras comes al día?", options: fruitsVegetables, field: "fruitsVegetables" },
    { Component: DropdownQuestion, question: "¿Cuántos días a la semana comes en restaurantes/fondas o pides comida?", options: eatingOut, field: "eatingOut" },
    { Component: DropdownQuestion, question: "¿Cuántos días a la semana tomas refresco, dulces, fritangas u otros productos chatarra?", options: junkFood, field: "junkFood" },
    {
      Component: SingleSelectWithFollowUp,
      question: "¿Fumas?",
      options: ["Sí", "No", "Dejé de fumar hace más de un año"],
      field: "smokes",
      followUp: {
        trigger: "Sí",
        question: "¿Cuántos cigarros a la semana fumas?",
        options: cigarettesPerWeek,
        field: "cigarettesPerWeek"
      }
    },
    {
      Component: SingleSelectWithFollowUp,
      question: "¿Consumes alcohol?",
      options: ["Sí", "No"],
      field: "alcoholConsumption",
      followUp: {
        trigger: "Sí",
        question: "¿Cuántas veces a la semana consumes alcohol?",
        options: alcoholFrequency,
        field: "alcoholFrequency"
      }
    },
    { Component: DropdownQuestion, question: "¿Tienes alguna otra adicción?", options: addictions, field: "addictions" },
    { Component: DropdownQuestion, question: "¿Cuántas horas a la semana le dedicas a tu bienestar emocional?", options: emotionalWellbeingHours, field: "emotionalWellbeingHours" },
    { Component: RatingQuestion, question: "Indica del 1 al 10 cuál consideras sea tu nivel de estrés.", maxRating: 10, field: "stressLevel" },
    { Component: DropdownQuestion, question: "¿Te sientes emocionalmente agotado?", options: frequencyOptions, field: "emotionalExhaustion" },
    { Component: DropdownQuestion, question: "¿Te sientes triste?", options: frequencyOptions, field: "sadness" },
    { Component: DropdownQuestion, question: "¿Te sientes frustrado?", options: frequencyOptions, field: "frustration" },
    { Component: RatingQuestion, question: "Indica del 1 al 10 qué tan realizado te sientes en tu trabajo.", maxRating: 10, field: "jobFulfillment" },
    { Component: RatingQuestion, question: "Indica del 1 al 10 qué tanto crees que tu empresa se preocupa por ti.", maxRating: 10, field: "companyCare" },
    { Component: RatingQuestion, question: "Indica del 1 al 10 qué tan feliz te sientes en tu vida.", maxRating: 10, field: "happiness" },
    { Component: DropdownQuestion, question: "En las últimas 4 semanas, ¿qué tan seguido has percibido estrés financiero?", options: frequencyFinancialStress, field: "frequencyFinancialStress" },
    { Component: DropdownQuestion, question: "¿Tienes contacto frecuente con amigos y familiares?", options: frequencyFriends, field: "frequencyFriends" },
    { Component: DropdownQuestion, question: "¿Participas en algún grupo o actividad social? (grupos de yoga, iglesia, voluntariado, talleres, actividades deportivas o culturales, asociaciones vecinales, etc.)", options: frequencySocial, field: "frequencySocial" },
    { Component: DropdownQuestion, question: "En general, ¿cómo consideras tu estado de salud?", options: healthAutoEvaluation, field: "healthAutoEvaluation" },
    { Component: DropdownQuestion, question: "¿Estás interesado en hacer un cambio en tus hábitos de salud y bienestar?", options: healthChangesInteres, field: "healthChangesInteres" },
    { Component: MultipleChoiceQuestion, question: "¿Te has realizado alguno de estos chequeos o medidas de prevención en los últimos 12 meses?", options: healtCheck, field: "healtCheck" }
  ];

  // Sincroniza refs/estado con el número real de preguntas
  useEffect(() => {
    questionsRef.current = questionsRef.current.slice(0, questions.length);
    setAnsweredState((prev) => {
      const next = new Array(questions.length).fill(false);
      prev.forEach((value, idx) => {
        if (idx < next.length && value) next[idx] = true;
      });
      return next;
    });
  }, [questions.length]);

 // Filtrar las dependientes si no hace ejercicio
  const filteredQuestions = questions.filter(q => {
    if (answers.physicalActivity === "No" && ["exerciseLocation", "exerciseHours"].includes(q.field)) {
      return false;
    }
    return true;
  });

  // Autocompletar defaults y marcar
  useEffect(() => {
    const locIdx = questions.findIndex(q => q.field === "exerciseLocation");
    const hrsIdx = questions.findIndex(q => q.field === "exerciseHours");
    if (answers.physicalActivity === "No") {
      setAnswers(prev => ({
        ...prev,
        exerciseLocation: "",
        exerciseHours: exerciseHours[0]
      }));
      setAnsweredState(prev => {
        const next = [...prev]; next[locIdx] = true; next[hrsIdx] = true; return next;
      });
    }
    if (answers.physicalActivity === "Sí") {
      setAnsweredState(prev => {
        const next = [...prev]; next[locIdx] = false; next[hrsIdx] = false; return next;
      });
      setAnswers(prev => { const copy = { ...prev }; delete copy.exerciseLocation; delete copy.exerciseHours; return copy; });
    }
  }, [answers.physicalActivity]);

    // Paginación con filteredQuestions
  const totalPages = Math.ceil(filteredQuestions.length / PAGE_SIZE);
  const start = currentPage * PAGE_SIZE;
  const pageQuestions = filteredQuestions.slice(start, start + PAGE_SIZE);

  // Determinar si la página está completa con base en answers
  const isPageComplete = pageQuestions.every((q) =>
    isQuestionAnswered(q, answers[q.field])
  );

  // Al cambiar de página, centrar la primera pregunta no respondida de la sección
  useEffect(() => {
    const firstUnq = pageQuestions.find(q =>
      !isQuestionAnswered(q, answers[q.field])
    );
    if (firstUnq) {
      const globalIdx = questions.findIndex(orig => orig.field === firstUnq.field);
      questionsRef.current[globalIdx]?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [currentPage]);

  // Porcentaje de progreso
  const progressPct = Math.round(((currentPage + 1) / totalPages) * 100);
  console.log("Página actual:", currentPage, "de", totalPages);
console.log("¿isPageComplete?", isPageComplete);
  return (
    <div className="questionnaire-container">
      <h2>Cuestionario de Salud HRA</h2>
      <p>Empresa: {companyName.name}</p>

      <div
        className="progress-fill"
        style={{ width: `${progressPct}%` }}
      />
      <p className="progress-text">Página {currentPage + 1} de {totalPages}</p>

      {pageQuestions.map((q, idx) => {
        const globalIdx = questions.findIndex((question) => question.field === q.field);
        const QuestionComp = q.Component;

        return (
          <div
            key={globalIdx}
            ref={el => (questionsRef.current[globalIdx] = el)}
            className="question-card"
          >
            <QuestionComp
              question={q.question}
              required
              type={q.type}
              options={q.options}
              optionA={q.optionA}
              optionB={q.optionB}
              followUp={q.followUp}
              maxRating={q.maxRating}
              hourRange={q.hourRange}
              value={answers[q.field] ?? ""}
              uniqueChoice={q.uniqueChoice}
              updateProgress={val => markQuestionAsAnswered(globalIdx, q.field, val)}
            />
          </div>
        );
      })}

      <div className="pagination">
        {currentPage > 0 && (
          <button onClick={handlePrev} className="page-button">
            Anterior
          </button>
        )}
        {currentPage < totalPages - 1 ? (
          <button
          onClick={handleNext}
          className="page-button"
          disabled={!isPageComplete}         // acá deshabilitas hasta que la página esté completa
        >
            Siguiente
          </button>
          
        ) : (
          <button onClick={handleSend} className="page-button submit-button" disabled={!isPageComplete}>
            Enviar
          </button>
        )}
      </div>
    </div>
  );
};
export default HRA;
